import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import PdfViewer from '../components/PdfViewer';

interface SongWithCharts {
  slug: string;
  available_charts: string[];
  has_lyrics: boolean;
}

interface ChartData {
  id: number;
  slug: string;
  pdf_path: string | null;
  title: string;
  artist: string;
  user_name: string;
  chart_type?: string;
  fallback?: boolean;
  lyrics?: string;
}

type Instrument = 'full' | 'bass' | 'guitar1' | 'guitar1_capo' | 'guitar2' | 'guitar2_capo' | 'keys' | 'drums' | 'fiddle';
type ViewMode = 'current' | 'next' | 'both';

const INSTRUMENTS: { value: Instrument; label: string }[] = [
  { value: 'full', label: 'Full Band' },
  { value: 'bass', label: 'Bass' },
  { value: 'guitar1', label: 'Guitar 1' },
  { value: 'guitar1_capo', label: 'Guitar 1 (Capo)' },
  { value: 'guitar2', label: 'Guitar 2' },
  { value: 'guitar2_capo', label: 'Guitar 2 (Capo)' },
  { value: 'keys', label: 'Keys' },
  { value: 'drums', label: 'Drums' },
  { value: 'fiddle', label: 'Fiddle / Mandolin' },
];

function makePdfUrl(pdfPath: string | null): string | null {
  if (!pdfPath) return null;
  return `${process.env.REACT_APP_API_URL || ''}${pdfPath}`;
}

const BandChartPage: React.FC = () => {
  const { member } = useAuth();
  const [currentData, setCurrentData] = useState<ChartData | null>(null);
  const [nextData, setNextData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState(true);
  const [showLyrics, setShowLyrics] = useState(false);
  const [lyricsUrl, setLyricsUrl] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('current');
  const defaultInstrument = (member?.instrument && INSTRUMENTS.some(i => i.value === member.instrument))
    ? member.instrument as Instrument
    : 'full';
  const [selectedInstrument, setSelectedInstrument] = useState<Instrument>(defaultInstrument);
  const [downloadProgress, setDownloadProgress] = useState<{ completed: number; total: number } | null>(null);
  const [downloadDone, setDownloadDone] = useState(false);

  // Check if charts are already cached on page load
  useEffect(() => {
    (async () => {
      try {
        const cache = await caches.open('charts-cache');
        const keys = await cache.keys();
        if (keys.length > 0) {
          setDownloadDone(true);
          setDownloadProgress({ completed: keys.length, total: keys.length });
        }
      } catch {}
    })();
  }, []);

  const downloadForOffline = useCallback(async () => {
    setDownloadProgress({ completed: 0, total: 0 });
    setDownloadDone(false);
    try {
      const res = await apiCall('/api/songs');
      if (!res.ok) return;
      const songs: SongWithCharts[] = await res.json();
      const baseUrl = process.env.REACT_APP_API_URL || '';
      const urls: string[] = [];
      for (const song of songs) {
        for (const instrument of (song.available_charts || [])) {
          urls.push(`${baseUrl}/charts/${song.slug}_${instrument}.pdf`);
        }
        // Also cache vocals PDF if the song has lyrics
        if (song.has_lyrics) {
          urls.push(`${baseUrl}/charts/${song.slug}_vocals.pdf`);
        }
      }
      setDownloadProgress({ completed: 0, total: urls.length });

      // Fetch all charts directly — the service worker's CacheFirst route
      // will automatically cache each one as it's fetched
      let completed = 0;
      const batchSize = 10;
      for (let i = 0; i < urls.length; i += batchSize) {
        const batch = urls.slice(i, i + batchSize);
        await Promise.all(batch.map(url => fetch(url).catch(() => {})));
        completed += batch.length;
        setDownloadProgress({ completed: Math.min(completed, urls.length), total: urls.length });
      }
      setDownloadDone(true);
    } catch {
      setDownloadProgress(null);
    }
  }, []);

  // Fetch current song chart
  useEffect(() => {
    let isInitialLoad = true;

    const fetchCurrentChart = async () => {
      if (isInitialLoad) setLoading(true);
      try {
        const res = await apiCall(`/api/songs/current-song-chart?instrument=${selectedInstrument}`);
        if (!res.ok) {
          if (res.status === 404) {
            setCurrentData(prev => prev !== null ? null : prev);
            if (isInitialLoad) setError('No song is currently playing.');
          } else {
            throw new Error('Failed to fetch current song chart');
          }
        } else {
          const data: ChartData = await res.json();
          setCurrentData(prev => {
            if (prev?.pdf_path === data.pdf_path && prev?.title === data.title && prev?.chart_type === data.chart_type) return prev;
            return data;
          });
          setError(null);
        }
      } catch (err: any) {
        // Only show error on initial load — don't clear existing data on poll failures
        if (isInitialLoad) setError(err.message || 'Unknown error');
      } finally {
        if (isInitialLoad) { setLoading(false); isInitialLoad = false; }
      }
    };

    fetchCurrentChart();
    const interval = setInterval(fetchCurrentChart, 5000);
    return () => clearInterval(interval);
  }, [selectedInstrument]);

  // Fetch next song chart when viewMode includes next
  useEffect(() => {
    if (viewMode === 'current') {
      setNextData(null);
      return;
    }

    const fetchNextChart = async () => {
      try {
        const res = await apiCall(`/api/songs/next-song-chart?instrument=${selectedInstrument}`);
        if (res.ok) {
          const data: ChartData = await res.json();
          setNextData(prev => {
            if (prev?.pdf_path === data.pdf_path && prev?.title === data.title && prev?.chart_type === data.chart_type) return prev;
            return data;
          });
        } else {
          setNextData(null);
        }
      } catch {
        setNextData(null);
      }
    };

    fetchNextChart();
    const interval = setInterval(fetchNextChart, 5000);
    return () => clearInterval(interval);
  }, [viewMode, selectedInstrument]);

  // Fetch vocals PDF URL when showLyrics is toggled on
  useEffect(() => {
    if (!showLyrics) { setLyricsUrl(null); return; }

    const fetchLyricsUrl = async () => {
      try {
        const res = await apiCall('/api/songs/current-song-chart?instrument=vocals');
        if (res.ok) {
          const data = await res.json();
          setLyricsUrl(data.pdf_path ? makePdfUrl(data.pdf_path) : null);
        }
      } catch { setLyricsUrl(null); }
    };

    fetchLyricsUrl();
    const interval = setInterval(fetchLyricsUrl, 5000);
    return () => clearInterval(interval);
  }, [showLyrics, currentData?.slug]);

  const currentPdfUrl = makePdfUrl(currentData?.pdf_path ?? null);
  const nextPdfUrl = makePdfUrl(nextData?.pdf_path ?? null);

  // Determine what to show in the main pane and optional second pane
  const showCurrent = viewMode === 'current' || viewMode === 'both';
  const showNext = viewMode === 'next' || viewMode === 'both';

  const selectStyle: React.CSSProperties = {
    padding: '10px 15px',
    fontSize: '16px',
    borderRadius: '4px',
    border: '1px solid #5a3a1a',
    backgroundColor: '#28190f',
    color: '#f5deb3',
    minWidth: '200px',
    cursor: 'pointer',
  };

  const btnBase: React.CSSProperties = {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 'bold',
    borderRadius: '6px',
    border: '1px solid #c8850f',
    background: 'linear-gradient(180deg, #c8850f 0%, #a06800 100%)',
    color: '#fff',
    cursor: 'pointer',
    letterSpacing: '0.5px',
    textTransform: 'uppercase' as const,
  };

  const btnActive: React.CSSProperties = {
    ...btnBase,
    background: 'linear-gradient(180deg, #a06800 0%, #7a5000 100%)',
    border: '2px solid #ffd700',
  };

  const smallBtnBase: React.CSSProperties = {
    marginTop: '8px',
    padding: '6px 10px',
    fontSize: '12px',
    fontWeight: 'bold',
    borderRadius: '4px',
    border: '1px solid #c8850f',
    background: 'linear-gradient(180deg, #c8850f 0%, #a06800 100%)',
    color: '#fff',
    cursor: 'pointer',
    width: '100%',
    letterSpacing: '0.5px',
    textTransform: 'uppercase' as const,
  };

  const smallBtnActive: React.CSSProperties = {
    ...smallBtnBase,
    background: 'linear-gradient(180deg, #a06800 0%, #7a5000 100%)',
    border: '2px solid #ffd700',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '11px', fontWeight: 'bold', color: '#c8850f',
    textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 4,
  };

  // Render a chart pane (used in both normal and split views)
  const renderChartPane = (data: ChartData | null, url: string | null, label: string, titleSuffix: string) => (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <div style={labelStyle}>{label}{data ? `: ${data.title}` : ''}</div>
      {data && (
        <p style={{ color: '#a08060', fontSize: '13px', margin: '0 0 4px 0' }}>
          {data.artist} -- {data.user_name}
        </p>
      )}
      {url ? (
        <PdfViewer
          src={url}
          title={titleSuffix}
          mode="paged"
          style={{ flex: 1, width: '100%', border: '1px solid #5a3a1a', borderRadius: '4px', backgroundColor: '#fff' }}
        />
      ) : (
        <p style={{ color: '#a08060', fontStyle: 'italic', textAlign: 'center', marginTop: 20 }}>
          {label === 'Next' ? 'No next song in queue' : 'No chart available'}
        </p>
      )}
    </div>
  );

  // ── FULLSCREEN MODE ──
  if (!showInfo) {
    // Determine fullscreen content
    const fsLeftUrl = showLyrics && lyricsUrl ? currentPdfUrl : (showCurrent ? currentPdfUrl : nextPdfUrl);
    const fsRightUrl = showLyrics && lyricsUrl ? lyricsUrl : (viewMode === 'both' ? nextPdfUrl : null);
    const hasSplit = fsRightUrl !== null;

    return (
      // Fullscreen shell: a flex column that owns the viewport. The bar takes
      // whatever height it needs and the chart gets the rest, so nothing has to
      // guess the bar's height (the old `top: 42` / `calc(100vh - 42px)` was a
      // guess, and an iPad renders the controls taller than that - pushing the
      // bottom of the chart off-screen). `100dvh` keeps the shell inside the
      // *visible* area on iOS Safari, where `100vh` includes the browser chrome;
      // browsers without dvh support drop it and fall back to the inset sizing.
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        height: '100dvh', zIndex: 9999,
        display: 'flex', flexDirection: 'column',
        backgroundColor: '#1a0f0a',
      }}>
        {/* Control bar — horizontal across the top */}
        <div style={{
          flexShrink: 0, zIndex: 1,
          backgroundColor: 'rgba(26,15,10,0.95)', padding: '8px 16px',
          borderBottom: '1px solid #5a3a1a', boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap',
        }}>
          <select
            value={selectedInstrument}
            onChange={(e) => setSelectedInstrument(e.target.value as Instrument)}
            style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '4px', border: '1px solid #5a3a1a', backgroundColor: '#28190f', color: '#f5deb3', cursor: 'pointer' }}
          >
            {INSTRUMENTS.map((inst) => (
              <option key={inst.value} value={inst.value}>{inst.label}</option>
            ))}
          </select>

          <div style={{ display: 'flex', gap: 0 }}>
            {(['current', 'next', 'both'] as ViewMode[]).map((mode, i) => (
              <button key={mode} onClick={() => setViewMode(mode)}
                style={{
                  ...(viewMode === mode ? smallBtnActive : smallBtnBase),
                  marginTop: 0, fontSize: '11px', padding: '6px 10px', width: 'auto',
                  borderRadius: i === 0 ? '4px 0 0 4px' : i === 2 ? '0 4px 4px 0' : '0',
                  borderRight: i < 2 ? 'none' : undefined,
                }}>
                {mode === 'current' ? 'Now' : mode === 'next' ? 'Next' : 'Both'}
              </button>
            ))}
          </div>

          <button onClick={() => setShowLyrics(v => !v)}
            style={{ ...(showLyrics ? smallBtnActive : smallBtnBase), marginTop: 0, fontSize: '11px', padding: '6px 10px', width: 'auto' }}>
            {showLyrics ? 'Hide Lyrics' : 'Lyrics'}
          </button>

          <div style={{ flex: 1 }} />

          <button onClick={() => setShowInfo(true)}
            style={{ ...smallBtnBase, marginTop: 0, fontSize: '11px', padding: '6px 10px', width: 'auto' }}>
            Exit
          </button>
        </div>

        {/* Chart area — takes exactly the space the bar leaves over.
            minHeight: 0 lets a flex child actually shrink so its own scrolling
            works, rather than growing past the bottom of the screen. */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          {hasSplit ? (
            <>
              {fsLeftUrl && <PdfViewer src={fsLeftUrl} title="Left Pane" mode="paged" style={{ flex: 1, minWidth: 0, height: '100%', border: 'none', backgroundColor: '#fff' }} />}
              {fsRightUrl && <PdfViewer src={fsRightUrl} title="Right Pane" mode="paged" style={{ flex: 1, minWidth: 0, height: '100%', border: 'none', backgroundColor: '#fff' }} />}
            </>
          ) : (
            fsLeftUrl && <PdfViewer src={fsLeftUrl} title="Fullscreen Chart" mode="paged" style={{ flex: 1, minWidth: 0, height: '100%', border: 'none', backgroundColor: '#fff' }} />
          )}
        </div>
      </div>
    );
  }

  // ── NORMAL MODE ──
  return (
    <div style={{ minHeight: '100vh', padding: '32px 16px' }}>
      <h1 style={{
        color: '#ffd700', textAlign: 'center', fontSize: '28px', fontWeight: 'bold',
        marginBottom: '24px', textShadow: '0 2px 8px rgba(0,0,0,0.5)', letterSpacing: '1px',
      }}>Band Chart View</h1>

      {/* Top controls */}
      <div style={{ textAlign: 'center', marginBottom: 16, display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => setShowInfo(false)} style={btnBase}>Fullscreen</button>

        {/* View mode segmented control */}
        <div style={{ display: 'flex', gap: 0 }}>
          {(['current', 'next', 'both'] as ViewMode[]).map((mode, i) => (
            <button key={mode} onClick={() => setViewMode(mode)}
              style={{
                ...(viewMode === mode ? btnActive : btnBase),
                borderRadius: i === 0 ? '6px 0 0 6px' : i === 2 ? '0 6px 6px 0' : '0',
                borderRight: i < 2 ? 'none' : undefined,
              }}>
              {mode === 'current' ? 'Current' : mode === 'next' ? 'Next' : 'Both'}
            </button>
          ))}
        </div>

        <button onClick={() => setShowLyrics(v => !v)} style={showLyrics ? btnActive : btnBase}>
          {showLyrics ? 'Hide Lyrics' : 'Show Lyrics'}
        </button>

        <button
          onClick={downloadForOffline}
          disabled={downloadProgress !== null && !downloadDone}
          style={downloadDone ? btnActive : btnBase}
        >
          {downloadDone
            ? 'Offline Ready'
            : downloadProgress
              ? `Downloading ${downloadProgress.completed}/${downloadProgress.total}`
              : 'Download for Offline'}
        </button>
      </div>

      {loading && <p style={{ color: '#a08060', textAlign: 'center', fontSize: '18px' }}>Loading chart...</p>}
      {error && <p style={{ color: '#e74c3c', textAlign: 'center', fontSize: '16px' }}>Error: {error}</p>}

      {!loading && !error && (currentData || nextData) && (
        <div style={{
          maxWidth: viewMode === 'both' || (showLyrics && lyricsUrl) ? '1400px' : '900px',
          margin: '0 auto', background: '#28190f', borderRadius: '8px',
          border: '1px solid #5a3a1a', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        }}>
          {/* Song info header — show for single view modes */}
          {viewMode !== 'both' && (
            <>
              <h2 style={{ color: '#ffd700', textAlign: 'center', fontSize: '24px', marginBottom: '4px', fontWeight: 'bold' }}>
                {(viewMode === 'current' ? currentData?.title : nextData?.title) || 'No song'}
              </h2>
              <p style={{ color: '#a08060', textAlign: 'center', fontSize: '16px', marginBottom: '20px' }}>
                {(viewMode === 'current' ? currentData?.artist : nextData?.artist) || ''} -- Requested by: {(viewMode === 'current' ? currentData?.user_name : nextData?.user_name) || ''}
              </p>
            </>
          )}

          {/* Instrument Selection */}
          <div style={{ marginBottom: 20 }}>
            <label htmlFor="instrument-select" style={{
              display: 'block', marginBottom: 8, fontWeight: 'bold', fontSize: '14px',
              color: '#c8850f', textTransform: 'uppercase', letterSpacing: '1px',
            }}>Select Instrument:</label>
            <select id="instrument-select" value={selectedInstrument}
              onChange={(e) => setSelectedInstrument(e.target.value as Instrument)} style={selectStyle}>
              {INSTRUMENTS.map((inst) => (
                <option key={inst.value} value={inst.value}>{inst.label}</option>
              ))}
            </select>
          </div>

          {/* Fallback warning */}
          {viewMode === 'current' && currentData?.fallback && (
            <div style={{
              padding: '10px 15px', backgroundColor: 'rgba(243,156,18,0.15)',
              border: '1px solid rgba(243,156,18,0.3)', borderRadius: '4px', marginBottom: '15px',
              color: '#f39c12', fontSize: '14px',
            }}>
              No {INSTRUMENTS.find(i => i.value === selectedInstrument)?.label} chart available - showing Full Band chart
            </div>
          )}

          {/* Chart content */}
          {(() => {
            // Determine what two panes to show (if any)
            const showLyricsSplit = showLyrics && lyricsUrl;

            if (viewMode === 'both') {
              return (
                <div style={{ display: 'flex', gap: 12, height: '80vh' }}>
                  {renderChartPane(currentData, currentPdfUrl, 'Current', 'Current Chart')}
                  {showLyricsSplit
                    ? renderChartPane(null, lyricsUrl, 'Lyrics', 'Lyrics')
                    : renderChartPane(nextData, nextPdfUrl, 'Next', 'Next Chart')
                  }
                </div>
              );
            }

            const activeData = viewMode === 'current' ? currentData : nextData;
            const activeUrl = viewMode === 'current' ? currentPdfUrl : nextPdfUrl;

            if (showLyricsSplit && activeUrl) {
              return (
                <div style={{ display: 'flex', gap: 12, height: '80vh' }}>
                  {renderChartPane(null, activeUrl, 'Chart', `${activeData?.title} Chart`)}
                  {renderChartPane(null, lyricsUrl, 'Lyrics', 'Lyrics')}
                </div>
              );
            }

            if (activeUrl) {
              return (
                <PdfViewer src={activeUrl}
                  title={`${activeData?.title} - ${activeData?.chart_type || selectedInstrument} Chart`}
                  mode="paged"
                  style={{ width: '100%', height: '80vh', border: '1px solid #5a3a1a', borderRadius: '4px', backgroundColor: '#fff' }}
                />
              );
            }

            return (
              <p style={{ color: '#a08060', fontStyle: 'italic', textAlign: 'center' }}>
                {viewMode === 'next' ? 'No next song in queue' : 'No chart available for this song.'}
              </p>
            );
          })()}
        </div>
      )}

      {!loading && !error && !currentData && !nextData && (
        <p style={{ color: '#a08060', textAlign: 'center', fontSize: '16px' }}>Waiting for a song to be marked as 'playing'...</p>
      )}
    </div>
  );
};

export default BandChartPage;
